import fetchClient from '@eureca/recruiter-api-sdk/client';
import { getKeycloak } from '@/auth/keycloak-instance';
import { recruiterEnv } from '@/config/env';

/**
 * Data layer do visualizador de currículo/laudo (EU-594, fatia 2).
 *
 * Os BYTES do PDF vêm de `GET /candidates/:userId/files/{resume|attestation}`
 * — o BFF streama `application/pdf` já com marca d'água server-side. O
 * `fetchClient` do SDK só decodifica JSON/texto, então o download usa `fetch`
 * cru carregando o mesmo bearer do Keycloak (mesma receita do
 * `fetchResumeBlob()` do backoffice) e vira Blob → object URL.
 *
 * EU-752 — o SPA NUNCA conclui ausência a partir de falha: "documento não
 * encontrado" é SÓ o `404` com `code: 'document_missing'` que o BFF emite
 * quando a key não existe no Core. Qualquer outro 404 (Core: candidato fora
 * do escopo/inexistente; ingress; key rejeitada) e qualquer 5xx/rede é ERRO
 * retentável — por isso o `code` do corpo de erro viaja no `DocumentFileError`.
 *
 * O compartilhamento (`POST /candidates/:userId/files/:kind/share`) é JSON
 * normal e segue pelo `fetchClient` raw path (endpoint ainda fora do SDK
 * tipado — mesma receita da candidate-profile-query).
 */

export type DocumentKind = 'resume' | 'attestation';

/** Códigos que o BFF manda no corpo de erro (aditivos ao `error` humano). */
export const DOCUMENT_MISSING_CODE = 'document_missing';
export const SHARING_UNCONFIGURED_CODE = 'sharing_unconfigured';

export class DocumentFileError extends Error {
  readonly name = 'DocumentFileError';
  constructor(
    message: string,
    readonly status: number,
    /** `code` do corpo de erro do BFF (`document_missing`, `storage_object_unreachable`, …) ou null. */
    readonly code: string | null = null
  ) {
    super(message);
  }
}

export class DocumentShareError extends Error {
  readonly name = 'DocumentShareError';
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null = null
  ) {
    super(message);
  }
}

/** Lê `code` do corpo JSON de erro do BFF; corpo ausente/não-JSON/sem code → null. */
export async function readErrorCode(res: Response): Promise<string | null> {
  try {
    const body: unknown = await res.json();
    const code = (body as { code?: unknown } | null)?.code;
    return typeof code === 'string' ? code : null;
  } catch {
    return null;
  }
}

/**
 * EU-752 — o ÚNICO caso que vira "Documento não encontrado": o BFF confirmou
 * que o candidato não tem a key no Core. 404 sem esse code (Core, ingress, key
 * rejeitada) é erro.
 */
export function isDocumentMissing(error: unknown): boolean {
  return (
    error instanceof DocumentFileError &&
    error.status === 404 &&
    error.code === DOCUMENT_MISSING_CODE
  );
}

/** Ambiente sem `DOC_SHARE_SECRET` — copy própria, não "tente novamente". */
export function isSharingUnconfigured(error: unknown): boolean {
  return error instanceof DocumentShareError && error.code === SHARING_UNCONFIGURED_CODE;
}

/**
 * Baixa um arquivo do candidato (currículo/laudo/avatar) e devolve um object
 * URL `blob:`. O CALLER é dono do URL e deve revogá-lo
 * (`URL.revokeObjectURL`) no cleanup. `avatar` entra aqui porque a foto
 * enviada pela plataforma vive no bucket privado e `<img src>` não manda
 * bearer — só URL externa de SSO dispensa este caminho.
 */
export async function fetchCandidateDocumentUrl(
  userId: string,
  kind: DocumentKind | 'avatar'
): Promise<string> {
  const keycloak = getKeycloak();
  try {
    await keycloak.updateToken(30);
  } catch {
    // Refresh falhou — deixa a request 401 e o estado de erro aparecer.
  }
  const token = keycloak.token;
  const base = recruiterEnv.apiUrl.replace(/\/+$/, '');
  const res = await fetch(`${base}/candidates/${userId}/files/${kind}`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new DocumentFileError(
      `Falha ao baixar o documento (HTTP ${res.status})`,
      res.status,
      await readErrorCode(res)
    );
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export interface ShareLinkResponse {
  /** URL pública temporária do documento (com marca d'água). */
  url: string;
  /** Instante ISO em que o link expira. */
  expiresAt: string;
}

/** TTLs aceitos pelo BFF (que clampa em 24–168h). */
export const SHARE_TTL_OPTIONS = [
  { value: 24, label: '24 horas' },
  { value: 72, label: '3 dias' },
  { value: 168, label: '7 dias' },
] as const;

/** Gera (ou re-gera) o link público temporário do documento. */
export async function shareCandidateDocument(
  userId: string,
  kind: DocumentKind,
  ttlHours: number
): Promise<ShareLinkResponse> {
  const res = await fetchClient<ShareLinkResponse>({
    method: 'POST',
    url: `/candidates/${userId}/files/${kind}/share`,
    data: { ttlHours },
  });
  if (res.error) {
    const code = (res.error.error as { code?: unknown } | undefined)?.code;
    throw new DocumentShareError(
      `Falha ao gerar o link de compartilhamento (status ${res.error.status})`,
      res.error.status,
      typeof code === 'string' ? code : null
    );
  }
  return res.data;
}
